import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { User, Plus, Search, Shield, Mail, MoreHorizontal, Edit2, Trash2, Key, Loader2, X, UserCog } from 'lucide-react';
import { formatStoreName } from '@/lib/storeUtils';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

const API_URL = getApiBaseUrl();

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    email: '', 
    role: 'staff',
    password: '',
    assigned_stores: [],
    allowed_production_sites: [],
    page_permissions: { garment_scheduler: false, inventory: false },
    is_active: true
  });
  const [bulkEditForm, setBulkEditForm] = useState({
    role: '',
    assigned_stores: [],
    allowed_production_sites: [],
    is_active: null
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch users and stores on mount
  useEffect(() => {
    fetchUsersAndStores();
  }, []);

  const fetchUsersAndStores = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [usersRes, storesRes, factoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/stores`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/factories?active_only=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }
      
      if (storesRes.ok) {
        const storesData = await storesRes.json();
        setStores(storesData);
      }

      if (factoriesRes.ok) {
        const factoriesData = await factoriesRes.json();
        setFactories(Array.isArray(factoriesData) ? factoriesData : []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditForm({
      name: '',
      email: '',
      role: 'staff',
      password: '',
      assigned_stores: [],
      allowed_production_sites: [],
      page_permissions: { garment_scheduler: false, inventory: false },
      is_active: true
    });
    setAddDialogOpen(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: '',
      assigned_stores: user.assigned_stores || [],
      allowed_production_sites: user.allowed_production_sites || [],
      page_permissions: {
        ...(user.page_permissions || {}),
        garment_scheduler: user.page_permissions?.garment_scheduler || false,
        inventory: user.page_permissions?.inventory || false,
      },
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleSaveAdd = async () => {
    if (!editForm.name || !editForm.email || !editForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          email: editForm.email,
          password: editForm.password,
          role: editForm.role,
          assigned_stores: editForm.assigned_stores,
          allowed_production_sites: editForm.allowed_production_sites,
          page_permissions: editForm.page_permissions,
          is_active: editForm.is_active
        })
      });

      if (res.ok) {
        toast.success('User created successfully');
        setAddDialogOpen(false);
        fetchUsersAndStores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name) {
      toast.error('Name is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const updateData = {
        name: editForm.name,
        role: editForm.role,
        assigned_stores: editForm.assigned_stores,
        allowed_production_sites: editForm.allowed_production_sites,
        page_permissions: editForm.page_permissions,
        is_active: editForm.is_active
      };

      // Only include password if it's been changed
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      const res = await fetch(`${API_URL}/api/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (res.ok) {
        toast.success('User updated successfully');
        setEditDialogOpen(false);
        fetchUsersAndStores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        toast.success('User permanently deleted');
        setDeleteDialogOpen(false);
        fetchUsersAndStores();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStore = (storeId) => {
    setEditForm(prev => ({
      ...prev,
      assigned_stores: prev.assigned_stores.includes(storeId)
        ? prev.assigned_stores.filter(id => id !== storeId)
        : [...prev.assigned_stores, storeId]
    }));
  };

  const toggleBulkStore = (storeId) => {
    setBulkEditForm(prev => ({
      ...prev,
      assigned_stores: prev.assigned_stores.includes(storeId)
        ? prev.assigned_stores.filter(id => id !== storeId)
        : [...prev.assigned_stores, storeId]
    }));
  };

  const toggleFactory = (factoryId) => {
    setEditForm(prev => ({
      ...prev,
      allowed_production_sites: prev.allowed_production_sites.includes(factoryId)
        ? prev.allowed_production_sites.filter(id => id !== factoryId)
        : [...prev.allowed_production_sites, factoryId]
    }));
  };

  const toggleBulkFactory = (factoryId) => {
    setBulkEditForm(prev => ({
      ...prev,
      allowed_production_sites: prev.allowed_production_sites.includes(factoryId)
        ? prev.allowed_production_sites.filter(id => id !== factoryId)
        : [...prev.allowed_production_sites, factoryId]
    }));
  };

  const handleResetPassword = (user) => {
    toast.info('Password reset feature coming soon');
  };

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      const selectableUsers = filteredUsers.map(u => u.user_id);
      setSelectedUserIds(selectableUsers);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUserIds(prev => [...prev, userId]);
    } else {
      setSelectedUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleBulkEditClick = () => {
    setBulkEditForm({
      role: '',
      assigned_stores: [],
      allowed_production_sites: [],
      is_active: null
    });
    setBulkEditDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkEdit = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Filter out empty values
      const updates = {};
      if (bulkEditForm.role) updates.role = bulkEditForm.role;
      if (bulkEditForm.assigned_stores.length > 0) updates.assigned_stores = bulkEditForm.assigned_stores;
      if (bulkEditForm.allowed_production_sites.length > 0) updates.allowed_production_sites = bulkEditForm.allowed_production_sites;
      if (bulkEditForm.is_active !== null) updates.is_active = bulkEditForm.is_active;

      if (Object.keys(updates).length === 0) {
        toast.error('Please select at least one field to update');
        return;
      }

      // Update each selected user
      const updatePromises = selectedUserIds.map(userId =>
        fetch(`${API_URL}/api/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        })
      );

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.ok).length;

      if (successCount === selectedUserIds.length) {
        toast.success(`${successCount} user(s) updated successfully`);
      } else {
        toast.warning(`${successCount} of ${selectedUserIds.length} user(s) updated`);
      }

      setBulkEditDialogOpen(false);
      setSelectedUserIds([]);
      fetchUsersAndStores();
    } catch (error) {
      console.error('Error bulk editing users:', error);
      toast.error('Failed to update users');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      // Delete each selected user
      const deletePromises = selectedUserIds.map(userId =>
        fetch(`${API_URL}/api/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r.ok).length;

      if (successCount === selectedUserIds.length) {
        toast.success(`${successCount} user(s) permanently deleted`);
      } else {
        toast.warning(`${successCount} of ${selectedUserIds.length} user(s) deleted`);
      }

      setBulkDeleteDialogOpen(false);
      setSelectedUserIds([]);
      fetchUsersAndStores();
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      toast.error('Failed to delete users');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700 border-purple-200',
      admin: 'bg-amber-100 text-amber-700 border-amber-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      kiosk: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      staff: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      production: 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeUsers = users.filter(u => u.is_active).length;
  const adminCount = users.filter(u => u.role === 'owner' || u.role === 'admin').length;
  const allSelected = filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length;

  return (
    <div className="space-y-6 p-3">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Users
          </h1>
          <p className="text-muted-foreground text-sm">Manage user accounts and access permissions</p>
        </div>
        <Button 
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={handleAddClick}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUserIds.length > 0 && (
        <Card className="border-primary bg-primary/5 shadow-sm rounded-xl">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="bg-primary text-primary-foreground">
                  {selectedUserIds.length} selected
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkEditClick}
                  className="h-8"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Bulk Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDeleteClick}
                  className="h-8 text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUserIds([])}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border bg-card shadow-sm rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Users</p>
                <p className="text-2xl font-semibold text-foreground mt-1">{users.length}</p>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-500/10">
                <User className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card shadow-sm rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Owners/Admins</p>
                <p className="text-2xl font-semibold text-foreground mt-1">{adminCount}</p>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10">
                <Shield className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card shadow-sm rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Active Users</p>
                <p className="text-2xl font-semibold text-foreground mt-1">{activeUsers}</p>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10">
                <Mail className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-border bg-card shadow-sm rounded-xl">
        <CardHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-medium text-foreground">All Users</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search users..." 
                className="pl-10 h-9 bg-background border-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all users"
                    />
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Name</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Role</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Stores</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Factories</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id} className="border-border hover:bg-muted/50 transition-colors">
                    <TableCell>
                      <Checkbox
                        checked={selectedUserIds.includes(user.user_id)}
                        onCheckedChange={(checked) => handleSelectUser(user.user_id, checked)}
                        aria-label={`Select ${user.name}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-foreground">{user.name}</TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getRoleBadgeColor(user.role)}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {user.assigned_stores.length === 0 ? (
                        <span className="text-xs italic">All stores</span>
                      ) : (
                        user.assigned_stores.map(formatStoreName).join(', ')
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm" data-testid={`user-factories-${user.user_id}`}>
                      {(user.allowed_production_sites || []).length === 0 ? (
                        <span className="text-xs italic">Store fallback</span>
                      ) : (
                        (user.allowed_production_sites || [])
                          .map((factoryId) => {
                            const factory = factories.find((entry) => entry.factory_id === factoryId);
                            return factory ? `${factory.factory_name} (${factory.factory_code})` : factoryId;
                          })
                          .join(', ')
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={user.is_active 
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                          : "bg-gray-100 text-gray-700 border-gray-200"
                        }
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" data-testid={`user-actions-${user.user_id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleEditClick(user)} className="cursor-pointer">
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(user)} className="cursor-pointer">
                            <Key className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(user)} 
                            className="cursor-pointer text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with role and store assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-name" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="h-9 bg-background border-input"
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-email" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="h-9 bg-background border-input"
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Password <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-password"
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="h-9 bg-background border-input"
                placeholder="Enter password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-role" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Role</Label>
              <select
                id="add-role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="staff">Staff</option>
                <option value="production">Production</option>
                <option value="manager">Manager</option>
                <option value="kiosk">Kiosk</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Assigned Stores
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background">
                {stores.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No stores available</p>
                ) : (
                  stores.map(store => (
                    <div key={store.store_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`add-store-${store.store_id}`}
                        checked={editForm.assigned_stores.includes(store.store_id)}
                        onCheckedChange={() => toggleStore(store.store_id)}
                      />
                      <label
                        htmlFor={`add-store-${store.store_id}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {formatStoreName(store.name)} ({store.location})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Select all stores to give access to all. Only Owner/Admin roles can see all stores without restriction.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Allowed Production Factories
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background" data-testid="add-user-factory-checkbox-group">
                {factories.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No factories available</p>
                ) : (
                  factories.map(factory => (
                    <div key={factory.factory_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`add-factory-${factory.factory_id}`}
                        checked={editForm.allowed_production_sites.includes(factory.factory_id)}
                        onCheckedChange={() => toggleFactory(factory.factory_id)}
                        data-testid={`add-user-factory-checkbox-${factory.factory_id}`}
                      />
                      <label htmlFor={`add-factory-${factory.factory_id}`} className="text-sm font-medium leading-none cursor-pointer">
                        {factory.factory_name} ({factory.factory_code})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  If none selected, garment visibility falls back to assigned stores.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Production Access
              </Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="add-production-access"
                  data-testid="add-production-access-checkbox"
                  checked={editForm.page_permissions?.garment_scheduler || false}
                  onCheckedChange={(checked) =>
                    setEditForm({
                      ...editForm,
                      page_permissions: {
                        ...editForm.page_permissions,
                        garment_scheduler: !!checked,
                      },
                    })
                  }
                />
                <label
                  htmlFor="add-production-access"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Allow access to Garment Scheduler tabs
                </label>
              </div>
              {editForm.role === 'manager' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="add-inventory-access"
                    data-testid="add-inventory-access-checkbox"
                    checked={editForm.page_permissions?.inventory || false}
                    onCheckedChange={(checked) =>
                      setEditForm({
                        ...editForm,
                        page_permissions: {
                          ...editForm.page_permissions,
                          inventory: !!checked,
                        },
                      })
                    }
                  />
                  <label
                    htmlFor="add-inventory-access"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Allow access to Inventory
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAdd} 
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details, role, and store assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="h-9 bg-background border-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Email (Read-only)
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                disabled
                className="h-9 bg-muted border-input cursor-not-allowed"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                New Password (Optional)
              </Label>
              <Input
                id="edit-password"
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="h-9 bg-background border-input"
                placeholder="Leave blank to keep current password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Role</Label>
              <select
                id="edit-role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="staff">Staff</option>
                <option value="production">Production</option>
                <option value="manager">Manager</option>
                <option value="kiosk">Kiosk</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Assigned Stores
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background">
                {stores.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No stores available</p>
                ) : (
                  stores.map(store => (
                    <div key={store.store_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-store-${store.store_id}`}
                        checked={editForm.assigned_stores.includes(store.store_id)}
                        onCheckedChange={() => toggleStore(store.store_id)}
                      />
                      <label
                        htmlFor={`edit-store-${store.store_id}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {formatStoreName(store.name)} ({store.location})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Select all stores to give access to all. Only Owner/Admin roles can see all stores without restriction.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Allowed Production Factories
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background" data-testid="edit-user-factory-checkbox-group">
                {factories.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No factories available</p>
                ) : (
                  factories.map(factory => (
                    <div key={factory.factory_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-factory-${factory.factory_id}`}
                        checked={editForm.allowed_production_sites.includes(factory.factory_id)}
                        onCheckedChange={() => toggleFactory(factory.factory_id)}
                        data-testid={`edit-user-factory-checkbox-${factory.factory_id}`}
                      />
                      <label htmlFor={`edit-factory-${factory.factory_id}`} className="text-sm font-medium leading-none cursor-pointer">
                        {factory.factory_name} ({factory.factory_code})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  If none selected, garment visibility falls back to assigned stores.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Production Access
              </Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-production-access"
                  data-testid="edit-production-access-checkbox"
                  checked={editForm.page_permissions?.garment_scheduler || false}
                  onCheckedChange={(checked) =>
                    setEditForm({
                      ...editForm,
                      page_permissions: {
                        ...editForm.page_permissions,
                        garment_scheduler: !!checked,
                      },
                    })
                  }
                />
                <label
                  htmlFor="edit-production-access"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Allow access to Garment Scheduler tabs
                </label>
              </div>
              {editForm.role === 'manager' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-inventory-access"
                    data-testid="edit-inventory-access-checkbox"
                    checked={editForm.page_permissions?.inventory || false}
                    onCheckedChange={(checked) =>
                      setEditForm({
                        ...editForm,
                        page_permissions: {
                          ...editForm.page_permissions,
                          inventory: !!checked,
                        },
                      })
                    }
                  />
                  <label
                    htmlFor="edit-inventory-access"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Allow access to Inventory
                  </label>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-active"
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
              />
              <label
                htmlFor="edit-active"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                User is active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong>? 
              This action cannot be undone and will remove all user data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Edit Users</DialogTitle>
            <DialogDescription>
              Update {selectedUserIds.length} selected user(s). Only filled fields will be updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-role" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Role (Optional)
              </Label>
              <select
                id="bulk-role"
                value={bulkEditForm.role}
                onChange={(e) => setBulkEditForm({ ...bulkEditForm, role: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">-- No Change --</option>
                <option value="staff">Staff</option>
                <option value="production">Production</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Assigned Stores (Optional)
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background">
                {stores.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No stores available</p>
                ) : (
                  stores.map(store => (
                    <div key={store.store_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`bulk-store-${store.store_id}`}
                        checked={bulkEditForm.assigned_stores.includes(store.store_id)}
                        onCheckedChange={() => toggleBulkStore(store.store_id)}
                      />
                      <label
                        htmlFor={`bulk-store-${store.store_id}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {store.name} ({store.location})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Leave unchecked to keep current store assignments
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Allowed Production Factories (Optional)
              </Label>
              <div className="space-y-2 border border-input rounded-md p-3 bg-background" data-testid="bulk-user-factory-checkbox-group">
                {factories.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No factories available</p>
                ) : (
                  factories.map(factory => (
                    <div key={factory.factory_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`bulk-factory-${factory.factory_id}`}
                        checked={bulkEditForm.allowed_production_sites.includes(factory.factory_id)}
                        onCheckedChange={() => toggleBulkFactory(factory.factory_id)}
                        data-testid={`bulk-user-factory-checkbox-${factory.factory_id}`}
                      />
                      <label
                        htmlFor={`bulk-factory-${factory.factory_id}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {factory.factory_name} ({factory.factory_code})
                      </label>
                    </div>
                  ))
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Leave unchecked to keep current factory assignments.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Status (Optional)
              </Label>
              <select
                value={bulkEditForm.is_active === null ? '' : bulkEditForm.is_active.toString()}
                onChange={(e) => setBulkEditForm({ 
                  ...bulkEditForm, 
                  is_active: e.target.value === '' ? null : e.target.value === 'true' 
                })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">-- No Change --</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkEdit} 
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Users'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Users</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{selectedUserIds.length} user(s)</strong>? 
              This action cannot be undone and will remove all user data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedUserIds.length} User(s)`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
